/* ==========================================================================
   MAISON MILAN — Cover carousel
   Slides are stacked and cross-faded rather than translated: the images are
   full-bleed and run under the masthead, so a sliding track would show its
   seams against the bar. Autoplay stops for anyone who has asked for less
   motion, and pauses on hover, focus and while the tab is hidden.
   ========================================================================== */
class MaisonCover extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    if (this.slides.length < 2) return;

    this.dots = Array.from(this.querySelectorAll('[data-cover-dot]'));
    this.index = 0;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.delay = parseInt(this.dataset.autoplay, 10) || 0;

    this.querySelector('[data-cover-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-cover-next]')?.addEventListener('click', () => this.step(1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.step(-1);
      if (event.key === 'ArrowRight') this.step(1);
    });

    // Pause whenever someone is reading or interacting, and while the tab is
    // in the background — an unattended carousel burns through its slides.
    ['mouseenter', 'focusin'].forEach((e) => this.addEventListener(e, () => this.stop()));
    ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => this.start()));
    document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));

    this.addSwipe();
    this.render();
    this.start();
  }

  disconnectedCallback() { this.stop(); }

  addSwipe() {
    let startX = null;
    this.addEventListener('pointerdown', (e) => { startX = e.clientX; }, { passive: true });
    this.addEventListener('pointerup', (e) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      startX = null;
      // Below this a drag is a mis-click, not a swipe.
      if (Math.abs(dx) > 48) this.step(dx < 0 ? 1 : -1);
    }, { passive: true });
  }

  step(direction) {
    this.goTo((this.index + direction + this.slides.length) % this.slides.length);
  }

  goTo(next) {
    this.index = next;
    this.render();
    this.start();
  }

  render() {
    this.slides.forEach((slide, i) => {
      const current = i === this.index;
      slide.classList.toggle('is-active', current);
      // Hidden slides leave the tab order as well as the accessibility tree,
      // or keyboard focus walks into links nobody can see.
      slide.toggleAttribute('aria-hidden', !current);
      slide.querySelectorAll('a, button').forEach((el) => {
        el.tabIndex = current ? 0 : -1;
      });
    });
    this.dots.forEach((dot, i) => {
      dot.setAttribute('aria-selected', i === this.index ? 'true' : 'false');
    });
  }

  start() {
    this.stop();
    if (!this.delay || this.reduced) return;
    this.timer = setInterval(() => this.step(1), this.delay);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('maison-cover')) {
  customElements.define('maison-cover', MaisonCover);
}
